/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMutation } from "@tanstack/react-query";
import { endpoints } from "../endpoints";
import { patchResource, postResource } from "../https";
import { 
  ForgotPasswordDTO, 
  LoginDTO, 
  RegisterDTO, 
  ResetPasswordDTO, 
  VerifyEmailDTO,
  ResendOtpDTO 
} from "../types";
import { useLocation, useNavigate } from "react-router-dom";
import { useToast } from "@/hooks";

export const login = (payload: LoginDTO) => {
  return postResource({ pathUrl: endpoints.login, payload });
};

export const register = (payload: RegisterDTO) => {
  return postResource({ pathUrl: endpoints.register, payload });
};

export const verifyEmail = (payload: VerifyEmailDTO) => {
  return postResource({ pathUrl: endpoints.verifyEmail, payload });
};

export const resendOtp = (payload: ResendOtpDTO) => {
  return postResource({ pathUrl: endpoints.resendOtp, payload });
};

export const forgotPassword = (payload: ForgotPasswordDTO) => {
  return postResource({ pathUrl: endpoints.forgotPassword, payload });
};

export const resetPassword = (payload: ResetPasswordDTO) => {
  return patchResource({ pathUrl: endpoints.resetPassword, payload });
};

export const useLogin = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (payload: LoginDTO) => login(payload),
    onSuccess: async (data) => {

      if (data) {
        try {
          localStorage.setItem("authToken", data.data.token);
          
          navigate(state?.from || "/", { replace: true });

        } catch (error) {
          console.error("Login failed:", error);
        }
      }
    },
    onError: (error: any) => {
      console.log(error, "ERR")
      toast({
        title: "Login failed!",
        description: `${error.message || "Something went wrong, please try again."}`,
        variant: "destructive",
      });
    },
  });
};

export const useRegister = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (payload: RegisterDTO) => register(payload),
    onSuccess: async (data) => {
      if (data.data.email) {
        try {
          // Store email for verification
          localStorage.setItem("pendingVerificationEmail", data.data.email);
          
          toast({
            title: "Registration successful!",
            description: "Please check your email for verification code.",
          });
          
          // Always redirect to email verification after registration
          navigate("/verify-email", { replace: true });
        } catch (error) {
          console.error("Registration failed:", error);
        }
      }
    },
    onError: (error: any) => {
      toast({
        title: "Registration failed!!",
        description: `${error.message}`,
        variant: "destructive",
      });
    },
  });
};

export const useVerifyEmail = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (payload: VerifyEmailDTO) => verifyEmail(payload),
    onSuccess: async (data) => {
      
      // Check the usage type to determine navigation
      const usage = localStorage.getItem("verificationUsage") || "VERIFY_EMAIL";
      
      if (usage === "RESET_PASSWORD") {
        // For password reset flow
        toast({
          title: "Email verified successfully!",
          description: "You can now reset your password.",
        });
        
        // Navigate to reset password page
        navigate("/reset-password", { replace: true });
      } else {
        // For email verification flow (registration)
        if (data.data.token) {
          try {
            localStorage.setItem("authToken", data.data.token);
            localStorage.removeItem("pendingVerificationEmail");
            localStorage.removeItem("verificationUsage");
            
            toast({
              title: "Email verified successfully!",
              description: "Welcome to StedShop!",
            });
            
            navigate(state?.from || "/", { replace: true });
          } catch (error) {
            console.error("Email verification failed:", error);
          }
        }
      }
    },
    onError: (error: any) => {
      toast({
        title: "Verification failed!",
        description: `${error.message}`,
        variant: "destructive",
      });
    },
  });
};

export const useResendOtp = () => {
  const { toast } = useToast();

  return useMutation({
    mutationFn: (payload: ResendOtpDTO) => resendOtp(payload),
    onSuccess: async (data) => {
      toast({
        title: "Code sent!",
        description: "A new verification code has been sent to your email.",
      });
      console.log(data, "D")

    },
    onError: (error: any) => {
      toast({
        title: "Failed to send code!",
        description: `${error.message}`,
        variant: "destructive",
      });
    },
  });
};

export const useForgotPassword = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (payload: ForgotPasswordDTO) => forgotPassword(payload),
    onSuccess: async () => {
      localStorage.setItem("verificationUsage", "RESET_PASSWORD");
      
      toast({
        title: "Verification code sent!",
        description: "Please check your email for the verification code.",
      });
      
      // Navigate to verify email page instead of login
      navigate("/verify-email", { replace: true });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to send reset email!",
        description: `${error.message}`,
        variant: "destructive",
      });
    },
  });
};

export const useResetPassword = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (payload: ResetPasswordDTO) => resetPassword(payload),
    onSuccess: async () => {
      // Clean up localStorage
      localStorage.removeItem("pendingVerificationEmail");
      localStorage.removeItem("verificationUsage");
      
      toast({
        title: "Password reset successful!",
        description: "Your password has been updated. Please login with your new password.",
      });
      
      navigate("/login", { replace: true });
    },
    onError: (error: any) => {
      toast({
        title: "Password reset failed!",
        description: `${error.message}`,
        variant: "destructive",
      });
    },
  });
};